export type PlaylistItemMode = 'NORMAL' | 'COMMON' | 'SCHEDULED'

export interface PlaylistRuleItem {
  mode?: PlaylistItemMode
  repeatEvery?: number | null
  activeStartTime?: string | null
  activeEndTime?: string | null
  activeTimezone?: string | null
}

function minutesInTimezone(now: Date, timezone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now)
    const value = (type: 'hour' | 'minute') =>
      Number(parts.find((part) => part.type === type)?.value ?? Number.NaN)
    const hour = value('hour')
    const minute = value('minute')
    return Number.isFinite(hour) && Number.isFinite(minute)
      ? hour * 60 + minute
      : null
  } catch {
    return null
  }
}

function parseTime(value: string | null | undefined): number | null {
  if (!value || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return null
  const [hour, minute] = value.split(':').map(Number)
  return hour! * 60 + minute!
}

export function scheduledPlaylistItemIsActive(
  item: PlaylistRuleItem,
  now: Date
): boolean {
  if ((item.mode ?? 'NORMAL') !== 'SCHEDULED') return true
  const current = minutesInTimezone(now, item.activeTimezone ?? '')
  const start = parseTime(item.activeStartTime)
  const end = parseTime(item.activeEndTime)
  if (current === null || start === null || end === null) return false
  if (start === end) return true
  return start < end
    ? current >= start && current < end
    : current >= start || current < end
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = Math.abs(left)
  let b = Math.abs(right)
  while (b) [a, b] = [b, a % b]
  return a || 1
}

export function buildPlaylistSequence<T extends PlaylistRuleItem>(
  items: T[],
  now: Date
): T[] {
  const common = items.find((item) => item.mode === 'COMMON')
  const regular = items.filter(
    (item) => item.mode !== 'COMMON' && scheduledPlaylistItemIsActive(item, now)
  )
  if (!common || regular.length === 0) return regular

  const repeatEvery = Math.max(1, Math.min(100, common.repeatEvery ?? 1))
  const regularCycleLength =
    (regular.length * repeatEvery) /
    greatestCommonDivisor(regular.length, repeatEvery)
  const sequence: T[] = []
  for (let index = 0; index < regularCycleLength; index += 1) {
    sequence.push(regular[index % regular.length]!)
    if ((index + 1) % repeatEvery === 0) sequence.push(common)
  }
  return sequence
}
